import { useEffect, useMemo, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { Empty } from "../components/ui";
import { aiIndicatorLabel } from "../lib/riskagent";
import type {
  RiskAgent, RiskNode, RiskDetectorNode, RiskActionNode, RiskFinding, Assignee, AiIndicator, SignalBand, TaskPriority, Workspace,
} from "../lib/types";
import * as work from "../lib/workClient";

/* The Risk Agent builder: a visual Watch → Detect → Act flow. Nodes live in
 * three lanes; edges wire sources into detectors and detectors into actions.
 * The right rail configures the selected node; the panel below shows a live
 * preview of the risk it surfaces right now. */

const LANES: { kind: RiskNode["kind"]; title: string; sub: string }[] = [
  { kind: "source", title: "Watch", sub: "Data sources" },
  { kind: "detector", title: "Detect", sub: "Rules & AI" },
  { kind: "action", title: "Act", sub: "What to do" },
];
const NODE_W = 224, NODE_H = 78, GAP_Y = 18, PAD = 18;
const LANE_X: Record<RiskNode["kind"], number> = { source: PAD, detector: PAD + 300, action: PAD + 600 };
const BAND_COLOR: Record<SignalBand, string> = { risk: "var(--risk)", watch: "var(--watch)", positive: "var(--good)" };
const AI_INDICATORS: AiIndicator[] = ["negative_sentiment", "competitor_mention", "sponsor_churn", "escalation", "churn_language", "adoption_risk"];
const encAssignee = (a: Assignee) => (a.kind === "role" ? `role:${a.role}` : `user:${a.userId}`);
const parseAssignee = (v: string): Assignee => { const [k, id] = v.split(":"); return k === "role" ? { kind: "role", role: id } : { kind: "user", userId: id }; };
const uid = (p: string) => `${p}_${Date.now().toString(36)}${Math.floor(Math.random() * 1e3).toString(36)}`;

interface Placed { node: RiskNode; x: number; y: number; }
function layout(agent: RiskAgent): { placed: Placed[]; height: number } {
  const placed: Placed[] = [];
  let height = 160;
  for (const lane of LANES) {
    const nodes = agent.nodes.filter((n) => n.kind === lane.kind);
    nodes.forEach((node, i) => {
      const y = PAD + i * (NODE_H + GAP_Y);
      placed.push({ node, x: LANE_X[lane.kind], y });
      height = Math.max(height, y + NODE_H + PAD);
    });
  }
  return { placed, height };
}

export default function RiskAgents() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [agents, setAgents] = useState<RiskAgent[]>(ws.riskAgents);
  const [selId, setSelId] = useState<string>(ws.riskAgents[0]?.id ?? "");
  const [nodeId, setNodeId] = useState<string | null>(null);
  const [linkFrom, setLinkFrom] = useState<string | null>(null);
  const [findings, setFindings] = useState<RiskFinding[]>([]);
  const [sources, setSources] = useState<{ id: string; label: string; unstructured: boolean }[]>([]);
  const [toast, setToast] = useState("");

  useEffect(() => {
    work.loadRiskAgents(ws).then((r) => { setMode(r.mode); setAgents(r.agents); if (!r.agents.some((a) => a.id === selId)) setSelId(r.agents[0]?.id ?? ""); });
    work.loadSources(ws).then(setSources);

  }, [ws]);

  const agent = agents.find((a) => a.id === selId) ?? null;
  const flash = (m: string) => { setToast(m); setTimeout(() => setToast(""), 2800); };

  // live preview whenever the selected agent changes
  useEffect(() => { if (agent) work.previewAgent(mode, ws, agent).then(setFindings); else setFindings([]); }, [agent, mode, ws]);

  const save = async (next: RiskAgent) => {
    setAgents((prev) => prev.map((a) => (a.id === next.id ? next : a)));
    await work.updateRiskAgent(mode, ws, next.id, { nodes: next.nodes, edges: next.edges, name: next.name, description: next.description, enabled: next.enabled });
  };
  const patchAgent = (p: Partial<RiskAgent>) => { if (agent) save({ ...agent, ...p }); };
  const patchNode = (id: string, p: Partial<RiskNode>) => { if (agent) save({ ...agent, nodes: agent.nodes.map((n) => (n.id === id ? { ...n, ...p } as RiskNode : n)) }); };

  const addNode = (node: RiskNode) => { if (agent) { save({ ...agent, nodes: [...agent.nodes, node] }); setNodeId(node.id); } };
  const removeNode = (id: string) => { if (agent) save({ ...agent, nodes: agent.nodes.filter((n) => n.id !== id), edges: agent.edges.filter((e) => e.from !== id && e.to !== id) }); if (nodeId === id) setNodeId(null); };
  const toggleEdge = (from: string, to: string) => {
    if (!agent) return;
    const exists = agent.edges.some((e) => e.from === from && e.to === to);
    save({ ...agent, edges: exists ? agent.edges.filter((e) => !(e.from === from && e.to === to)) : [...agent.edges, { from, to }] });
  };

  const onNodeClick = (n: RiskNode) => {
    if (linkFrom && linkFrom !== n.id) {
      const src = agent!.nodes.find((x) => x.id === linkFrom)!;
      const validPair = (src.kind === "source" && n.kind === "detector") || (src.kind === "detector" && n.kind === "action");
      if (validPair) toggleEdge(linkFrom, n.id);
      setLinkFrom(null);
      return;
    }
    setNodeId(n.id);
  };

  const create = async () => {
    const { ent } = await work.loadEntitlements(ws);
    if (ent.agentsUsed >= ent.agentLimit) { flash(`You've used all ${ent.agentLimit} risk agents on your plan — add a slot in Settings → Usage.`); return; }
    const a = await work.createRiskAgent(mode, ws, { name: "New risk agent", enabled: false, nodes: [], edges: [] });
    setAgents((p) => [...p, a]); setSelId(a.id); setNodeId(null);
  };
  const remove = async (a: RiskAgent) => { setAgents((p) => p.filter((x) => x.id !== a.id)); if (selId === a.id) setSelId(""); await work.deleteRiskAgent(mode, ws, a.id); };
  const run = async () => {
    if (!agent) return;
    const r = await work.runRiskAgent(mode, ws, agent);
    setFindings(r.findings);
    flash(r.signalsRaised + r.tasksCreated === 0 ? "No new risk — nothing raised" : `Raised ${r.signalsRaised} signal${r.signalsRaised === 1 ? "" : "s"} · created ${r.tasksCreated} task${r.tasksCreated === 1 ? "" : "s"}`);
  };

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Risk Agents</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Build a risk agent</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>
            A visual flow that watches your data, detects risk — deterministic rules or AI over conversations —
            and acts: raise a signal, open a task, notify an owner. No code.
          </p>
        </div>
        <ModePill mode={mode} />
      </div>

      <div className="ra-layout">
        <div className="ra-list">
          <div className="row between" style={{ marginBottom: 10 }}>
            <span className="eyebrow" style={{ margin: 0 }}>Agents</span>
            <button className="mini-btn" title="New agent" onClick={create}><Icon name="plus" size={13} /></button>
          </div>
          {agents.length === 0 ? <Empty>None yet</Empty> : agents.map((a) => (
            <button key={a.id} className={`ra-list__item ${a.id === selId ? "active" : ""}`} onClick={() => { setSelId(a.id); setNodeId(null); }}>
              <span className={`ra-dot ${a.enabled ? "on" : ""}`} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <b>{a.name}</b>
                <small>{a.enabled ? "Active" : "Paused"} · {a.nodes.length} nodes</small>
              </span>
            </button>
          ))}
        </div>

        <div>
          {!agent ? (
            <div className="card"><div className="card__bd"><Empty>Select an agent, or create one.</Empty></div></div>
          ) : (
            <>
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card__bd">
                  <div className="row between" style={{ alignItems: "flex-start", gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <input className="inp inp--title" value={agent.name} onChange={(e) => patchAgent({ name: e.target.value })} />
                      <input className="inp" style={{ marginTop: 6, border: "none", padding: "2px 0", fontSize: 13, color: "var(--muted)" }}
                        placeholder="What does this agent watch for?" value={agent.description ?? ""} onChange={(e) => patchAgent({ description: e.target.value })} />
                    </div>
                    <div className="row" style={{ gap: 10 }}>
                      <label className="row" style={{ gap: 7, fontSize: 12.5 }}>
                        <button className="toggle" role="switch" aria-checked={agent.enabled} onClick={() => patchAgent({ enabled: !agent.enabled })}>
                          <span className={`toggle__track ${agent.enabled ? "on" : ""}`}><span className="toggle__knob" /></span>
                        </button>
                        <span className="faint">{agent.enabled ? "Active" : "Paused"}</span>
                      </label>
                      <button className="btn btn--primary btn--sm" onClick={run}><Icon name="bolt" size={14} /> Run now</button>
                      <button className="icon-btn" title="Delete agent" onClick={() => remove(agent)}><Icon name="dot" size={16} /></button>
                    </div>
                  </div>
                </div>
              </div>

              {toast && <div className="toast"><Icon name="check" size={14} /> {toast}</div>}

              {linkFrom && <div className="ra-hint"><Icon name="link" size={13} /> Click a {agent.nodes.find((n) => n.id === linkFrom)?.kind === "source" ? "detector" : "action"} to connect · <button className="linklike" onClick={() => setLinkFrom(null)}>cancel</button></div>}

              <Canvas agent={agent} sources={sources} selectedId={nodeId} linkFrom={linkFrom}
                onNodeClick={onNodeClick} onAdd={addNode} onStartLink={setLinkFrom} onRemoveNode={removeNode} onToggleEdge={toggleEdge} />

              {nodeId && agent.nodes.find((n) => n.id === nodeId) && (
                <Inspector ws={ws} sources={sources} node={agent.nodes.find((n) => n.id === nodeId)!} onPatch={(p) => patchNode(nodeId, p)} onClose={() => setNodeId(null)} />
              )}

              <FindingsPanel findings={findings} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Canvas({ agent, sources, selectedId, linkFrom, onNodeClick, onAdd, onStartLink, onRemoveNode, onToggleEdge }: {
  agent: RiskAgent; sources: { id: string; label: string; unstructured: boolean }[]; selectedId: string | null; linkFrom: string | null;
  onNodeClick: (n: RiskNode) => void; onAdd: (n: RiskNode) => void; onStartLink: (id: string) => void; onRemoveNode: (id: string) => void; onToggleEdge: (f: string, t: string) => void;
}) {
  const { placed, height } = useMemo(() => layout(agent), [agent]);
  const pos = new Map(placed.map((p) => [p.node.id, p]));
  const anchor = (id: string, side: "in" | "out") => { const p = pos.get(id); if (!p) return null; return { x: p.x + (side === "out" ? NODE_W : 0), y: p.y + NODE_H / 2 }; };

  return (
    <div className="ra-canvas-wrap">
      <div className="ra-lanes">
        {LANES.map((l) => (
          <div key={l.kind} className="ra-lane-hd" style={{ left: LANE_X[l.kind] }}>
            <b>{l.title}</b><small>{l.sub}</small>
            <AddNode kind={l.kind} sources={sources} onAdd={onAdd} />
          </div>
        ))}
      </div>
      <div className="ra-canvas" style={{ height }}>
        <svg className="ra-edges" width="100%" height={height}>
          {agent.edges.map((e, i) => {
            const a = anchor(e.from, "out"), b = anchor(e.to, "in");
            if (!a || !b) return null;
            const mx = (a.x + b.x) / 2;
            return <path key={i} className="ra-edge" d={`M ${a.x} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${b.x} ${b.y}`}
              onClick={() => onToggleEdge(e.from, e.to)}><title>Click to disconnect</title></path>;
          })}
        </svg>
        {placed.map(({ node, x, y }) => (
          <NodeCard key={node.id} node={node} x={x} y={y} sources={sources}
            selected={selectedId === node.id} linking={linkFrom === node.id}
            onClick={() => onNodeClick(node)} onStartLink={() => onStartLink(node.id)} onRemove={() => onRemoveNode(node.id)} />
        ))}
      </div>
    </div>
  );
}

function NodeCard({ node, x, y, sources, selected, linking, onClick, onStartLink, onRemove }: {
  node: RiskNode; x: number; y: number; sources: { id: string; label: string; unstructured: boolean }[];
  selected: boolean; linking: boolean; onClick: () => void; onStartLink: () => void; onRemove: () => void;
}) {
  const meta = nodeMeta(node, sources);
  return (
    <div className={`ra-node ra-node--${node.kind} ${selected ? "sel" : ""} ${linking ? "linking" : ""}`} style={{ left: x, top: y, width: NODE_W, height: NODE_H }} onClick={onClick}>
      {node.kind !== "source" && <span className="ra-port ra-port--in" />}
      <span className="ra-node__ic" style={{ color: meta.color }}><Icon name={meta.icon} size={15} /></span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="ra-node__title">{meta.title}</div>
        <div className="ra-node__sub">{meta.sub}</div>
      </div>
      {meta.band && <span className="ra-band" style={{ background: BAND_COLOR[meta.band] }} title={`${meta.band} band`} />}
      <button className="ra-x" title="Remove" onClick={(e) => { e.stopPropagation(); onRemove(); }}>×</button>
      {node.kind !== "action" && <button className="ra-port ra-port--out" title="Connect" onClick={(e) => { e.stopPropagation(); onStartLink(); }} />}
    </div>
  );
}

function nodeMeta(node: RiskNode, sources: { id: string; label: string }[]): { icon: string; title: string; sub: string; color: string; band?: SignalBand } {
  if (node.kind === "source") return { icon: "database", title: sources.find((s) => s.id === node.source)?.label ?? node.source, sub: "Watch source", color: "var(--ink-2)" };
  if (node.kind === "detector") {
    if (node.mode === "ai") return { icon: "sparkle", title: node.label, sub: `AI · ${aiIndicatorLabel(node.indicator)}`, color: "var(--corona)", band: node.band };
    return { icon: "filter", title: node.label, sub: `Rule · ${node.conditions.length} condition${node.conditions.length === 1 ? "" : "s"}`, color: "var(--ink-2)", band: node.band };
  }
  const t = node.type;
  return {
    icon: t === "raise_signal" ? "pulse" : t === "create_task" ? "check" : "bell",
    title: t === "raise_signal" ? "Raise signal" : t === "create_task" ? "Create task" : "Notify",
    sub: t === "notify" ? "Ping an owner" : t === "create_task" ? "Assign work" : "Flag the account", color: "var(--ink-2)",
  };
}

function AddNode({ kind, sources, onAdd }: { kind: RiskNode["kind"]; sources: { id: string; label: string; unstructured: boolean }[]; onAdd: (n: RiskNode) => void }) {
  const [open, setOpen] = useState(false);
  const make = (choice: string) => {
    const id = uid(kind);
    if (kind === "source") onAdd({ id, kind: "source", source: choice });
    else if (kind === "detector") {
      if (choice === "ai") onAdd({ id, kind: "detector", mode: "ai", label: "Negative sentiment", indicator: "negative_sentiment", minConfidence: 0.6, band: "risk" });
      else onAdd({ id, kind: "detector", mode: "rule", label: "New rule", conditions: [{ field: "health", op: "lt", value: 45 }], band: "risk" });
    } else {
      if (choice === "raise_signal") onAdd({ id, kind: "action", type: "raise_signal", title: "Risk detected — {account}" });
      else if (choice === "create_task") onAdd({ id, kind: "action", type: "create_task", title: "Follow up on {account}", assignee: { kind: "role", role: "csm" }, priority: "high", dueInDays: 2 });
      else onAdd({ id, kind: "action", type: "notify", target: { kind: "role", role: "csm" }, message: "Risk on {account}" });
    }
    setOpen(false);
  };
  const opts: { v: string; l: string }[] =
    kind === "source" ? sources.map((s) => ({ v: s.id, l: s.label + (s.unstructured ? " · text" : "") }))
    : kind === "detector" ? [{ v: "ai", l: "AI detector" }, { v: "rule", l: "Rule detector" }]
    : [{ v: "raise_signal", l: "Raise signal" }, { v: "create_task", l: "Create task" }, { v: "notify", l: "Notify" }];
  return (
    <span className="ra-add">
      <button className="ra-add__btn" onClick={() => setOpen((o) => !o)}><Icon name="plus" size={12} /> Add</button>
      {open && <><div className="ra-add__scrim" onClick={() => setOpen(false)} /><div className="ra-add__menu">{opts.map((o) => <button key={o.v} onClick={() => make(o.v)}>{o.l}</button>)}</div></>}
    </span>
  );
}

function Inspector({ ws, sources, node, onPatch, onClose }: {
  ws: Workspace; sources: { id: string; label: string; unstructured: boolean }[]; node: RiskNode; onPatch: (p: Partial<RiskNode>) => void; onClose: () => void;
}) {
  return (
    <div className="card ra-inspector">
      <div className="card__hd"><h3>Configure · {node.kind}</h3><button className="icon-btn" onClick={onClose}><Icon name="dot" size={15} /></button></div>
      <div className="card__bd" style={{ display: "grid", gap: 12 }}>
        {node.kind === "source" && (
          <Labeled label="Data source">
            <select className="inp" value={node.source} onChange={(e) => onPatch({ source: e.target.value })}>
              {sources.map((s) => <option key={s.id} value={s.id}>{s.label}{s.unstructured ? " (text — AI)" : ""}</option>)}
            </select>
          </Labeled>
        )}
        {node.kind === "detector" && <DetectorFields node={node} onPatch={onPatch} />}
        {node.kind === "action" && <ActionFields ws={ws} node={node} onPatch={onPatch} />}
      </div>
    </div>
  );
}

function DetectorFields({ node, onPatch }: { node: RiskDetectorNode; onPatch: (p: Partial<RiskNode>) => void }) {
  return (
    <>
      <div className="composer__grid" style={{ gridTemplateColumns: "1fr 140px" }}>
        <Labeled label="Label"><input className="inp" value={node.label} onChange={(e) => onPatch({ label: e.target.value })} /></Labeled>
        <Labeled label="Raise as"><select className="inp" value={node.band} onChange={(e) => onPatch({ band: e.target.value as SignalBand })}><option value="risk">Risk</option><option value="watch">Watch</option><option value="positive">Positive</option></select></Labeled>
      </div>
      {node.mode === "ai" ? (
        <div className="composer__grid" style={{ gridTemplateColumns: "1fr 160px" }}>
          <Labeled label="AI looks for">
            <select className="inp" value={node.indicator} onChange={(e) => onPatch({ indicator: e.target.value as AiIndicator, label: aiIndicatorLabel(e.target.value as AiIndicator) })}>
              {AI_INDICATORS.map((i) => <option key={i} value={i}>{aiIndicatorLabel(i)}</option>)}
            </select>
          </Labeled>
          <Labeled label={`Min confidence · ${Math.round(node.minConfidence * 100)}%`}>
            <input className="inp" type="range" min={0} max={1} step={0.05} value={node.minConfidence} onChange={(e) => onPatch({ minConfidence: Number(e.target.value) })} />
          </Labeled>
        </div>
      ) : (
        <ConditionRows node={node} onPatch={onPatch} />
      )}
      {node.mode === "ai" && <div className="ra-note"><Icon name="sparkle" size={12} /> Reads extracted sentiment & themes today; plug in an LLM over raw text with an API key.</div>}
    </>
  );
}

function ConditionRows({ node, onPatch }: { node: Extract<RiskDetectorNode, { mode: "rule" }>; onPatch: (p: Partial<RiskNode>) => void }) {
  const FIELDS = ["health", "arr", "region", "lifecycleStage", "segment", "expansionPotential", "seatsActive"];
  const OPS = [["lt", "<"], ["lte", "≤"], ["gt", ">"], ["gte", "≥"], ["eq", "is"], ["neq", "is not"], ["contains", "contains"]];
  const set = (cs: typeof node.conditions) => onPatch({ conditions: cs });
  return (
    <Labeled label="Conditions (all must hold)">
      <div style={{ display: "grid", gap: 8 }}>
        {node.conditions.map((c, i) => (
          <div key={i} className="rule-row">
            <select className="inp" value={c.field} onChange={(e) => set(node.conditions.map((x, j) => j === i ? { ...x, field: e.target.value } : x))}>{FIELDS.map((f) => <option key={f}>{f}</option>)}</select>
            <select className="inp inp--op" value={c.op} onChange={(e) => set(node.conditions.map((x, j) => j === i ? { ...x, op: e.target.value as typeof c.op } : x))}>{OPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <input className="inp" value={String(c.value)} onChange={(e) => set(node.conditions.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} />
            <button className="mini-btn" onClick={() => set(node.conditions.filter((_, j) => j !== i))}><Icon name="dot" size={13} /></button>
          </div>
        ))}
        <button className="btn btn--sm btn--ghost" style={{ justifySelf: "start" }} onClick={() => set([...node.conditions, { field: "health", op: "lt", value: 45 }])}><Icon name="plus" size={13} /> Add condition</button>
      </div>
    </Labeled>
  );
}

function ActionFields({ ws, node, onPatch }: { ws: Workspace; node: RiskActionNode; onPatch: (p: Partial<RiskNode>) => void }) {
  const AssigneeSel = ({ value, onChange }: { value: Assignee; onChange: (a: Assignee) => void }) => (
    <select className="inp" value={encAssignee(value)} onChange={(e) => onChange(parseAssignee(e.target.value))}>
      <optgroup label="Roles">{ws.roles.map((r) => <option key={r.key} value={`role:${r.key}`}>{r.label}</option>)}</optgroup>
      <optgroup label="People">{ws.users.map((u) => <option key={u.id} value={`user:${u.id}`}>{u.name}</option>)}</optgroup>
    </select>
  );
  if (node.type === "raise_signal") return <Labeled label="Signal title — use {account}"><input className="inp" value={node.title} onChange={(e) => onPatch({ title: e.target.value })} /></Labeled>;
  if (node.type === "create_task") return (
    <>
      <Labeled label="Task title — use {account}"><input className="inp" value={node.title} onChange={(e) => onPatch({ title: e.target.value })} /></Labeled>
      <div className="composer__grid" style={{ gridTemplateColumns: "1fr 120px 120px" }}>
        <Labeled label="Assign to"><AssigneeSel value={node.assignee} onChange={(a) => onPatch({ assignee: a })} /></Labeled>
        <Labeled label="Priority"><select className="inp" value={node.priority} onChange={(e) => onPatch({ priority: e.target.value as TaskPriority })}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></Labeled>
        <Labeled label="Due (days)"><input className="inp inp--num" type="number" value={node.dueInDays} onChange={(e) => onPatch({ dueInDays: Number(e.target.value) })} /></Labeled>
      </div>
    </>
  );
  return (
    <>
      <Labeled label="Notify"><AssigneeSel value={node.target} onChange={(a) => onPatch({ target: a })} /></Labeled>
      <Labeled label="Message — use {account}"><input className="inp" value={node.message} onChange={(e) => onPatch({ message: e.target.value })} /></Labeled>
    </>
  );
}

function FindingsPanel({ findings }: { findings: RiskFinding[] }) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card__hd">
        <div><h3>Live preview</h3><span className="sub">What this agent surfaces right now</span></div>
        <span className="chip chip--active">{findings.length} finding{findings.length === 1 ? "" : "s"}</span>
      </div>
      <div className="card__bd">
        {findings.length === 0 ? <Empty>No risk detected — wire a source into a detector, or adjust thresholds.</Empty> : (
          <div className="ra-findings">
            {findings.slice(0, 12).map((f, i) => (
              <div key={i} className="ra-finding">
                <span className="ra-band" style={{ background: BAND_COLOR[f.band] }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="row" style={{ gap: 8 }}><b style={{ fontSize: 13.5 }}>{f.accountName}</b><span className="tag">{f.detectorLabel}</span><span className="faint" style={{ fontSize: 12 }}>{Math.round(f.confidence * 100)}%</span></div>
                  <div className="faint" style={{ fontSize: 12.5, marginTop: 2 }}>{f.reason}</div>
                  <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: "wrap" }}>{f.actions.map((a, j) => <span key={j} className="ra-act-chip"><Icon name={a.type === "create_task" ? "check" : a.type === "notify" ? "bell" : "pulse"} size={11} /> {a.summary}</span>)}</div>
                </div>
              </div>
            ))}
            {findings.length > 12 && <div className="faint" style={{ fontSize: 12, padding: "4px 2px" }}>+ {findings.length - 12} more</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="labeled"><span className="labeled__l">{label}</span>{children}</label>;
}
function ModePill({ mode }: { mode: work.Mode }) {
  return <span className={`mode-pill ${mode === "api" ? "live" : ""}`} title={mode === "api" ? "Connected to the Penumbrai API" : "Standalone demo — changes saved to this browser"}><i />{mode === "api" ? "Live API" : "Local demo"}</span>;
}
